import { isPhotoTextZone } from '../../../utils/canvas';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { isEditorOnlyCanvasObject } from '../../../utils/canvas-utils/is-editor-only-canvas-object';

type PreviewableFabricObject = fabric.Object & {
  data?: { type?: CanvasDataTypes };
  visible?: boolean;
  getObjects?: () => PreviewableFabricObject[];
  _objects?: PreviewableFabricObject[];
};

type SnapshotOptions = { format?: string; multiplier?: number; quality?: number };

type PreviewSnapshotExportDimensions = { width: number; height: number };

type PreviewSnapshotOptions = {
  exportDimensions?: PreviewSnapshotExportDimensions;
  resetViewport?: boolean;
};

export type PreviewableCanvas = fabric.Canvas & {
  toDataURL?: (options?: SnapshotOptions) => string;
  renderAll?: () => void;
  requestRenderAll?: () => void;
  getWidth?: () => number;
  getHeight?: () => number;
  setDimensions?: (dimensions: PreviewSnapshotExportDimensions) => void;
  setViewportTransform?: (viewportTransform: number[]) => void;
  viewportTransform?: number[];
};

export type PreviewSnapshot = {
  dataURL: string;
  dimensions?: { width: number; height: number };
};

type HiddenObject = {
  object: PreviewableFabricObject;
  visible?: boolean;
};

type SnapshotViewportState = {
  dimensions?: PreviewSnapshotExportDimensions;
  viewportTransform?: number[];
};

const SNAPSHOT_OPTIONS: SnapshotOptions = { format: 'png', multiplier: 2 };
const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';
const PNG_DIMENSION_HEADER_LENGTH = 64;

/**
 * Gets child objects from a PhotoTextZone group.
 *
 * @param object Potential PhotoTextZone group
 * @returns Group children, or an empty array for non-PhotoTextZone objects
 */
const getGroupObjects = (object: PreviewableFabricObject): PreviewableFabricObject[] => {
  const isPhotoTextZoneObject =
    object.type === 'group' &&
    (isPhotoTextZone(object as fabric.Object) || object.data?.type === CanvasDataTypes.PhotoTextZone);
  if (!isPhotoTextZoneObject) return [];
  if (typeof object.getObjects === 'function') return object.getObjects();
  return object._objects ?? [];
};

/**
 * Determines which PhotoTextZone children are editor-only.
 *
 * S&S text lives inside a group. We hide the non-text controls/backgrounds
 * without destructively ungrouping the textbox.
 *
 * @param object Child object inside a PhotoTextZone group
 * @returns True when the child should be hidden for preview
 */
const shouldHidePhotoTextZoneChild = (object: PreviewableFabricObject): boolean => {
  if (object.type === 'textbox' || object.data?.type === CanvasDataTypes.PhotoZoneTextbox) {
    return false;
  }
  return true;
};

/**
 * Temporarily hides Fabric objects while preserving their original visibility.
 *
 * @param objects Fabric objects to hide
 * @returns Visibility snapshots used for restoration
 */
const hideObjects = (objects: PreviewableFabricObject[]): HiddenObject[] => {
  const hiddenObjects = objects.map((object) => ({ object, visible: object.visible }));
  hiddenObjects.forEach(({ object }) => {
    object.visible = false;
  });
  return hiddenObjects;
};

/**
 * Restores Fabric object visibility after snapshot capture.
 *
 * @param objects Visibility snapshots returned by hideObjects
 */
const restoreObjects = (objects: HiddenObject[]) => {
  objects.forEach(({ object, visible }) => {
    object.visible = visible;
  });
};

/**
 * Reads PNG dimensions from a data URL header.
 *
 * Fabric returns only a data URL, so parsing the PNG IHDR dimensions keeps
 * CardPreview aspect ratios aligned with the actual exported bitmap.
 *
 * @param dataURL PNG data URL generated by Fabric
 * @returns Pixel dimensions from the PNG header, or undefined when unreadable
 */
const getPngDataURLDimensions = (dataURL: string): { width: number; height: number } | undefined => {
  if (!dataURL.startsWith(PNG_DATA_URL_PREFIX) || typeof globalThis.atob !== 'function') {
    return undefined;
  }

  try {
    const header = globalThis.atob(
      dataURL.slice(PNG_DATA_URL_PREFIX.length, PNG_DATA_URL_PREFIX.length + PNG_DIMENSION_HEADER_LENGTH),
    );
    if (header.length < 24) return undefined;

    const readUInt32 = (offset: number): number =>
      ((header.charCodeAt(offset) & 0xff) << 24) |
      ((header.charCodeAt(offset + 1) & 0xff) << 16) |
      ((header.charCodeAt(offset + 2) & 0xff) << 8) |
      (header.charCodeAt(offset + 3) & 0xff);

    return { width: readUInt32(16), height: readUInt32(20) };
  } catch {
    return undefined;
  }
};

/**
 * Reads current Fabric canvas dimensions.
 *
 * @param canvas Live Fabric canvas
 * @returns Canvas dimensions when available
 */
const getCanvasDimensions = (
  canvas: PreviewableCanvas | null | undefined,
): PreviewSnapshotExportDimensions | undefined => {
  if (!canvas) return undefined;
  const width = typeof canvas.getWidth === 'function' ? canvas.getWidth() : undefined;
  const height = typeof canvas.getHeight === 'function' ? canvas.getHeight() : undefined;
  if (!width || !height) return undefined;
  return { width, height };
};

/**
 * Normalizes requested snapshot export dimensions to valid whole pixels.
 *
 * @param dimensions Requested export dimensions
 * @returns Whole-pixel dimensions, or undefined when invalid
 */
const getValidExportDimensions = (
  dimensions: PreviewSnapshotExportDimensions | undefined,
): PreviewSnapshotExportDimensions | undefined => {
  if (!dimensions) return undefined;
  const width = Math.ceil(dimensions.width);
  const height = Math.ceil(dimensions.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }
  return { width, height };
};

/**
 * Temporarily changes the canvas viewport for snapshot capture.
 *
 * S&S uses this to export from source-size coordinates instead of the scaled
 * editor viewport, avoiding seams from fractional zoomed canvas dimensions.
 *
 * @param canvas Live Fabric canvas
 * @param options Optional export dimensions and viewport reset flag
 * @returns Previous viewport state used for restoration
 */
const applySnapshotViewport = (
  canvas: PreviewableCanvas,
  options: PreviewSnapshotOptions | undefined,
): SnapshotViewportState | undefined => {
  const exportDimensions = getValidExportDimensions(options?.exportDimensions);
  if (!exportDimensions || typeof canvas.setDimensions !== 'function') {
    return undefined;
  }

  const viewportState = {
    dimensions: getCanvasDimensions(canvas),
    viewportTransform: canvas.viewportTransform ? [...canvas.viewportTransform] : undefined,
  };

  try {
    canvas.setDimensions(exportDimensions);
    if (options?.resetViewport && typeof canvas.setViewportTransform === 'function') {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    }
  } catch (error) {
    restoreSnapshotViewport(canvas, viewportState);
    throw error;
  }

  return viewportState;
};

/**
 * Restores the canvas dimensions and viewport after snapshot capture.
 *
 * @param canvas Live Fabric canvas
 * @param viewportState Previous viewport state returned by applySnapshotViewport
 */
const restoreSnapshotViewport = (canvas: PreviewableCanvas, viewportState: SnapshotViewportState | undefined) => {
  if (!viewportState) return;

  if (viewportState.dimensions && typeof canvas.setDimensions === 'function') {
    canvas.setDimensions(viewportState.dimensions);
  }

  if (viewportState.viewportTransform && typeof canvas.setViewportTransform === 'function') {
    canvas.setViewportTransform(viewportState.viewportTransform);
  }
};

/**
 * Takes a snapshot of the live Fabric canvas without mutating its object list.
 * Editor-only chrome (photozone buttons, edit icons, fold lines, etc.) is
 * hidden via `visible = false`, snapshotted, then restored.
 *
 * Existing production preview cleaners remove/ungroup objects from the canvas,
 * which is unsafe for quick preview because it runs against the live editor.
 * This helper owns only the non-destructive Fabric capture step; state mapping
 * and labels stay in createCardPreviewState.
 *
 * @param canvas Live Fabric canvas to capture
 * @param options Optional source-size export settings
 * @returns Snapshot data URL and bitmap dimensions, or undefined when unavailable
 */
export const createPreviewSnapshot = (
  canvas: PreviewableCanvas | null | undefined,
  options?: PreviewSnapshotOptions,
): PreviewSnapshot | undefined => {
  if (!canvas || typeof canvas.toDataURL !== 'function' || typeof canvas.getObjects !== 'function') {
    return undefined;
  }

  const editorChrome = canvas
    .getObjects()
    .filter((object) => isEditorOnlyCanvasObject(object as fabric.Object)) as PreviewableFabricObject[];
  const photoTextZoneChrome = canvas
    .getObjects()
    .flatMap((object) => getGroupObjects(object as PreviewableFabricObject))
    .filter(shouldHidePhotoTextZoneChild);

  let hiddenEditorChrome: HiddenObject[] = [];
  let hiddenPhotoTextZoneChrome: HiddenObject[] = [];
  let viewportState: SnapshotViewportState | undefined;

  try {
    hiddenEditorChrome = hideObjects(editorChrome);
    hiddenPhotoTextZoneChrome = hideObjects(photoTextZoneChrome);
    viewportState = applySnapshotViewport(canvas, options);
    canvas.renderAll?.();

    const dataURL = canvas.toDataURL(SNAPSHOT_OPTIONS);
    return {
      dataURL,
      dimensions: getPngDataURLDimensions(dataURL) ?? getCanvasDimensions(canvas),
    };
  } finally {
    try {
      restoreSnapshotViewport(canvas, viewportState);
    } finally {
      restoreObjects(hiddenEditorChrome);
      restoreObjects(hiddenPhotoTextZoneChrome);
      canvas.requestRenderAll?.();
    }
  }
};
